import {
  Activity,
  Alarm,
  AuditLog,
  Backup,
  Auth,
  Config,
  Contact,
  ContactGroup,
  Device,
  Event,
  EventMapping,
  Location,
  Notification,
  Room,
  Scanner,
  User,
  createRestClient,
} from '@evac/shared'
import { resetLoadedEntities } from '@/router/config'
import { resetDashboardLoaded } from '@/router/index'

type Endpoints = ReturnType<typeof createEndpoints>

let _endpoints: Endpoints | null = null

function createEndpoints(client: ReturnType<typeof createRestClient>) {
  return {
    device: Device.rest.createEndpoint(client),
    location: Location.rest.createEndpoint(client),
    room: Room.rest.createEndpoint(client),
    scanner: Scanner.rest.createEndpoint(client),
    alarm: Alarm.rest.createEndpoint(client),
    notification: Notification.rest.createEndpoint(client),
    event: Event.rest.createEndpoint(client),
    contact: Contact.rest.createEndpoint(client),
    contactGroup: ContactGroup.rest.createEndpoint(client),
    activity: { fetchAll: () => Activity.rest.fetchAll() },
    eventMapping: { fetchAll: () => EventMapping.rest.fetchAll() },
    config: { fetchAll: () => Config.rest.fetchAll() },
    backup: { fetchAll: () => Backup.rest.fetchAll() },
    user: { fetchAll: () => User.fetchAll() },
  }
}

export function initializeRestConnector() {
  const client = createRestClient({
    tokenProvider: Auth.getCurrentToken,
    onUnauthorized: () => {
      resetLoadedEntities()
      resetDashboardLoaded()
      Auth.logout()
    },
  })

  _endpoints = createEndpoints(client)

  Device.rest.bindActions(_endpoints.device)
  Location.rest.bindActions(_endpoints.location)
  Room.rest.bindActions(_endpoints.room)
  Scanner.rest.bindActions(_endpoints.scanner)
  Alarm.rest.bindActions(_endpoints.alarm)
  Notification.rest.bindActions(_endpoints.notification)
  Event.rest.bindActions(_endpoints.event)
  Event.rest.init(client)
  Contact.rest.bindActions(_endpoints.contact)
  ContactGroup.rest.bindActions(_endpoints.contactGroup)
  Activity.rest.init(client)
  EventMapping.rest.init(client)
  Config.rest.init(client)
  Backup.rest.init(client)
  AuditLog.rest.init(client)
  User.init(User.rest.createRestApi(client))

  return _endpoints
}

export function getEndpoints(): Endpoints {
  if (!_endpoints) throw new Error('REST endpoints not initialized. Call initializeRestConnector() first.')
  return _endpoints
}

export type { Endpoints }
